import { cn, XIcon } from "@au-schein-de/au-ui-library";
import { cva, type VariantProps } from "class-variance-authority";
import type { FocusEvent, ReactNode } from "react";

export const snackbarVariants = cva(
	"flex flex-col gap-1 overflow-hidden rounded-lg p-4 shadow-[0px_10px_20px_0px_rgba(33,42,54,0.16)]",
	{
		variants: {
			variant: {
				default: "bg-gray-700",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

export interface SnackbarProps extends VariantProps<typeof snackbarVariants> {
	message: ReactNode;
	layout?: "stacked" | "inline";
	actionLabel?: string;
	dismissAriaLabel?: string;
	onAction?: () => void | Promise<void>;
	onDismiss?: () => void | Promise<void>;
	onMouseEnter?: () => void;
	onMouseLeave?: () => void;
	onFocus?: () => void;
	onBlur?: () => void;
	className?: string;
}

function handleRootFocusChange(
	event: FocusEvent<HTMLElement>,
	handler?: () => void,
): void {
	if (
		event.relatedTarget instanceof Node &&
		event.currentTarget.contains(event.relatedTarget)
	) {
		return;
	}
	handler?.();
}

/** Dismissible notification bar with optional action button and hover/focus-pause support. */
export function Snackbar({
	message,
	layout = "stacked",
	actionLabel,
	dismissAriaLabel = "Dismiss",
	onAction,
	onDismiss,
	onMouseEnter,
	onMouseLeave,
	onFocus,
	onBlur,
	variant,
	className,
}: SnackbarProps) {
	const isInline = layout === "inline";
	const actionButton =
		actionLabel && onAction ? (
			<button
				type="button"
				onClick={onAction}
				className={cn(
					"text-ansay-light text-body-bold py-3 cursor-pointer hover:opacity-80",
					isInline ? "whitespace-nowrap" : "text-left",
				)}
				data-testid="snackbar-action"
			>
				{actionLabel}
			</button>
		) : null;

	return (
		<output
			className={cn(snackbarVariants({ variant }), className)}
			data-testid="snackbar"
			onMouseEnter={onMouseEnter}
			onMouseLeave={onMouseLeave}
			onFocus={(event) => handleRootFocusChange(event, onFocus)}
			onBlur={(event) => handleRootFocusChange(event, onBlur)}
		>
			<div
				className={cn(
					"flex gap-3 w-full",
					isInline ? "items-center" : "items-start",
				)}
			>
				<div className="flex-1 text-body text-gray-300">{message}</div>
				{isInline && actionButton}
				{onDismiss && (
					<button
						type="button"
						onClick={onDismiss}
						className="shrink-0 flex items-center justify-center size-10 cursor-pointer text-gray-300 hover:text-white"
						data-testid="snackbar-dismiss"
						aria-label={dismissAriaLabel}
					>
						<XIcon.Raw className="size-5" />
					</button>
				)}
			</div>
			{!isInline && actionButton}
		</output>
	);
}
